<script>
import Product from '@phoenix/mixins/components/common/Product';
import PictureLazy from '@maison/components/common/PictureLazy';
import WishlistButton from '@maison/components/wishlist/WishlistButton';
import { PhxIcon, SimpleSlider } from '@phx-styleguides/jlc';
import Fragment from '@phoenix/components/common/Fragment';

export default {
  components: {
    PictureLazy,
    WishlistButton,
    PhxIcon,
    Fragment,
  },

  mixins: [Product],

  props: {
    title: {
      type: String,
      required: true,
    },
    navigationTitle: {
      type: String,
      default: '',
    },
    displayClasses: {
      type: Array,
      default: () => [],
    },
    labels: {
      type: Array,
      default: () => [],
    },
    sliderImages: {
      type: Array,
      default: () => [],
    },
    enableSlider: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['productClick'],

  computed: {
    containerClasses() {
      return ['product-card'].concat(this.displayClasses);
    },

    hasSliderImages() {
      return this.enableSlider && this.sliderImages && this.sliderImages.length > 1;
    },
  },

  mounted() {
    if (this.hasSliderImages && this.$refs.productCardSlider) {
      new SimpleSlider(this.$refs.productCardSlider, {
        navigation: {
          prev: this.$refs.productCardSliderPrev,
          next: this.$refs.productCardSliderNext,
        },
        pagination: this.$refs.productCardSliderPagination,
      });
    }
  },

  methods: {
    getType(isLink) {
      return isLink ? 'a' : Fragment;
    },

    getAttrs(isLink, withSlider) {
      if (isLink) {
        return {
          href: this.url,
          class: ['product-card__link', { 'expand-target': !withSlider }],
          'data-tracking': this.dataTracking ? JSON.stringify(this.dataTracking) : null,
        };
      }

      return {};
    },

    onProductClick() {
      this.$emit('productClick');
    },
  },
};
</script>

<template>
  <div ref="productCard" :class="containerClasses" data-cy="product-card" @click="onProductClick">
    <component :is="getType(hasSliderImages)" v-bind="getAttrs(hasSliderImages, true)">
      <div
        v-if="hasSliderImages"
        ref="productCardSlider"
        class="simple-slider product-card__slider"
        style="pointer-events: auto"
      >
        <div class="simple-slider__body">
          <div v-for="image in sliderImages" :key="image.image.src" class="simple-slider__slide product-card__picture">
            <div class="picture">
              <picture-lazy v-bind="image" />
            </div>
          </div>
        </div>

        <div ref="productCardSliderPagination" class="product-card__slider-pagination"></div>
      </div>

      <div v-else class="product-card__picture">
        <div class="picture">
          <picture-lazy v-bind="image" img-class="product__img" />
        </div>
      </div>

      <div class="product-card__body">
        <component :is="getType(!hasSliderImages)" v-bind="getAttrs(!hasSliderImages, false)">
          <div class="product-card__details">
            <div v-if="labels.length" class="product-card__tags" data-cy="product-tags">
              <span
                v-for="(label, index) in labels"
                :key="index"
                :class="{
                  'tag product-card__tag': true,
                  'max-sm:hidden': index !== 0,
                  'tag--paladium': label.isPalladiumTag,
                  'tag--secondary': label.isSecondaryTag,
                }"
              >
                {{ label.value }}
              </span>
            </div>

            <h5 class="product-card__collection">{{ navigationTitle ? navigationTitle : title }}</h5>
            <h5 class="product-card__name">{{ name }}</h5>

            <div class="product-card__specs">{{ description }}</div>

            <div class="product-price product-card__price product-price--thin product-price--small">
              <product-price v-bind="localizedPrice" :should-display-from-price="shouldDisplayFromPrice" />
            </div>
          </div>
        </component>
      </div>
    </component>

    <div v-if="shouldDisplayAddToWishlist" class="product-card__actions">
      <wishlist-button :reference="modelReference" :tracking="tracking" />
    </div>

    <div v-if="hasSliderImages" class="product-card__slider-nav-wrapper">
      <button
        ref="productCardSliderPrev"
        type="button"
        class="btn btn--default btn--md btn--icon-only btn--icon btn btn--default product-card__slider-nav product-card__slider-nav--prev"
      >
        <span class="sr-only">
          {{ $t('Accessibility.product.discoverMore') }}
        </span>
        <span class="btn__icon">
          <phx-icon icon="chevron-left-16" bidirectional />
        </span>
      </button>
      <button
        ref="productCardSliderNext"
        type="button"
        class="btn btn--default btn--md btn--icon-only btn--icon btn btn--default product-card__slider-nav product-card__slider-nav--next"
      >
        <span class="sr-only">
          {{ $t('Accessibility.product.discoverMore') }}
        </span>
        <span class="btn__icon">
          <phx-icon icon="chevron-16" bidirectional />
        </span>
      </button>
    </div>
  </div>
</template>
